import { useCallback, useEffect, useRef } from "react";

/** The imperative half of a zustand store hook (`getState` / `subscribe`). */
interface StoreApi<S> {
  getState: () => S;
  subscribe: (listener: (state: S, prev: S) => void) => () => void;
}

export interface DataWatch {
  subscribe: (onChange: () => void) => () => void;
}

/** Watch one store for IDENTITY changes in the selected slices. Build these at
 *  module level (store hooks are singletons) so the watch array stays stable. */
export function watchSlices<S>(store: StoreApi<S>, select: (state: S) => readonly unknown[]): DataWatch {
  return {
    subscribe(onChange) {
      let prev = select(store.getState());
      return store.subscribe((state) => {
        const next = select(state);
        if (next.length === prev.length && next.every((value, i) => value === prev[i])) return;
        prev = next;
        onChange();
      });
    },
  };
}

export interface DataChangeHandle {
  /** Run a pending effect NOW; no-op when nothing is scheduled. */
  flush: () => void;
  /** Drop a pending effect without running it. */
  cancel: () => void;
  /** Request a debounced run for a change the watches can't see (e.g. an input
   *  that lives in React state), reusing the same timer. */
  schedule: () => void;
}

/**
 * Run `effect` (debounced) whenever a watched data slice changes, WITHOUT
 * subscribing the calling component to that data.
 *
 * This exists for the always-mounted background reconcilers in `Shell`
 * (reminders, widgets, sync). Selecting `tasks`/`rules`/`overrides` into their
 * render made every task edit re-render the Shell — and with it the sidebar and
 * the entire routed subtree — which is ~200ms of blocking work per edit on a
 * board-sized dataset, felt directly as drag/drop lag. They only ever read the
 * data inside a debounced effect, so an imperative subscription is enough.
 *
 * `effect` is read from a ref at fire time; it must pull fresh data from
 * `getState()` rather than close over a render's snapshot.
 */
export function useDataChangeEffect(
  watches: ReadonlyArray<DataWatch>,
  effect: () => void,
  { debounceMs, enabled = true, runOnEnable = false }: {
    debounceMs: number;
    enabled?: boolean;
    /** Also schedule one run when the subscription (re)starts. */
    runOnEnable?: boolean;
  },
): DataChangeHandle {
  const latest = useRef(effect);
  latest.current = effect;
  const controls = useRef<DataChangeHandle>({ flush: () => {}, cancel: () => {}, schedule: () => {} });

  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const clear = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const schedule = () => {
      clear();
      timer = setTimeout(() => {
        timer = null;
        latest.current();
      }, debounceMs);
    };
    controls.current = {
      flush: () => {
        if (!timer) return;
        clear();
        latest.current();
      },
      cancel: clear,
      schedule,
    };
    const unsubscribes = watches.map((watch) => watch.subscribe(schedule));
    if (runOnEnable) schedule();
    return () => {
      clear();
      controls.current = { flush: () => {}, cancel: () => {}, schedule: () => {} };
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [enabled, debounceMs, runOnEnable, watches]);

  const flush = useCallback(() => controls.current.flush(), []);
  const cancel = useCallback(() => controls.current.cancel(), []);
  const schedule = useCallback(() => controls.current.schedule(), []);
  return { flush, cancel, schedule };
}
